/**
 * Wedding slug: `emma-liam-2027-06-12`.
 *
 * PRD §F2 makes this immutable once the first email is sent, because it becomes
 * the Gmail label name and a matching signal. Renaming it afterwards would
 * orphan the label the planner's mailbox already carries.
 *
 * Lives in `shared` so the create form can show the planner the slug they are
 * about to commit to, computed by the same code the server will run.
 */

function slugifyToken(raw: string): string {
  return raw
    .normalize('NFKD')
    // Strip combining marks so "Nguyễn" becomes "nguyen" rather than dropping
    // the vowel entirely.
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * `date` is already the calendar date the slug wants — `weddingDate` is a
 * Postgres `date` column and travels as `2027-06-12`. No parsing, so no
 * timezone can shift the day out from under a planner in Sydney.
 */
function formatDatePart(date: string | null | undefined): string | null {
  if (!date) return null;
  const m = date.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1]! : null;
}

export function buildWeddingSlug(
  coupleNames: string,
  weddingDate: string | null | undefined,
): string {
  const namePart = slugifyToken(coupleNames) || 'wedding';
  const datePart = formatDatePart(weddingDate);
  return datePart ? `${namePart}-${datePart}` : namePart;
}

/**
 * Appends `-2`, `-3`, … until the slug is free. The caller supplies the taken
 * set; uniqueness is still enforced by the DB index, this just avoids losing a
 * round-trip to a constraint violation on the common case.
 */
export function ensureUniqueSlug(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Gmail label for a wedding: `VendorOps/emma-liam-2027-06-12`. */
export function labelNameForSlug(slug: string, prefix = 'VendorOps'): string {
  return `${prefix}/${slug}`;
}
